export const STAFF_WORKERS_PAGE_SIZE = 25;
export const STAFF_WORKER_SESSIONS_PAGE_SIZE = 100;

export const STAFF_WORKER_STATUS = {
    checkedIn: "checked_in",
    checkedOut: "checked_out",
    notCheckedIn: "not_checked_in",
    missedCheckout: "missed_checkout",
    failedCheckout: "failed_checkout",
};

export const STAFF_WORKER_STATUS_TRANSLATIONS = {
    [ STAFF_WORKER_STATUS.checkedIn ]: "STAFF_MANAGEMENT.WorkersStatus_CheckedIn",
    [ STAFF_WORKER_STATUS.checkedOut ]: "STAFF_MANAGEMENT.WorkersStatus_CheckedOut",
    [ STAFF_WORKER_STATUS.notCheckedIn ]: "STAFF_MANAGEMENT.WorkersStatus_NotCheckedIn",
    [ STAFF_WORKER_STATUS.missedCheckout ]: "STAFF_MANAGEMENT.AttendanceStatus_MissedCheckout",
    [ STAFF_WORKER_STATUS.failedCheckout ]: "STAFF_MANAGEMENT.AttendanceStatus_FailedCheckout",
};

const WORKER_STATUS_PRIORITY = {
    [ STAFF_WORKER_STATUS.checkedOut ]: 0,
    [ STAFF_WORKER_STATUS.checkedIn ]: 1,
    [ STAFF_WORKER_STATUS.missedCheckout ]: 2,
    [ STAFF_WORKER_STATUS.failedCheckout ]: 3,
};


const normalizeID = (value) => {
    const normalizedValue = String(value ?? "").trim();

    return normalizedValue || undefined;
};

export const normalizeStaffWorkerRoleIDs = (roles = []) => (
    [ ...new Set(
        (Array.isArray(roles) ? roles : [])
            .map((role) => normalizeID(role?.id ?? role))
            .filter(Boolean)
    ) ].sort((left, right) => left.localeCompare(right, undefined, { numeric: true }))
);

export const getStaffWorkerRoleColor = (colorHex) => {
    const normalizedColor = String(colorHex || "").trim();

    return /^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(normalizedColor) ?
        normalizedColor :
        "#FFFFFF";
};

export const buildStaffWorkersWhere = ({
    eligibleRoleIDs,
    selectedRoleID,
    subcontractorID,
    statusWhere,
} = {}) => {
    const normalizedEligibleRoleIDs = normalizeStaffWorkerRoleIDs(eligibleRoleIDs);
    const normalizedSelectedRoleID = normalizeID(selectedRoleID);
    const hasSelectedEligibleRole = (
        normalizedSelectedRoleID &&
        normalizedEligibleRoleIDs.includes(normalizedSelectedRoleID)
    );
    let where = hasSelectedEligibleRole ? {
        column: "ROLE_ID",
        operator: "IN",
        value: [ normalizedSelectedRoleID ],
    } : undefined;
    const normalizedSubcontractorID = normalizeID(subcontractorID);

    if (normalizedSubcontractorID)
    {
        const subcontractorWhere = {
            column: "SUBCONTRACTOR_ID",
            operator: "EQ",
            value: normalizedSubcontractorID,
        };

        where = where ? { ...where, AND: [ subcontractorWhere ] } : subcontractorWhere;
    }

    return statusWhere ? (where ? { AND: [ where, statusWhere ] } : statusWhere) : where;
};

export const getStaffWorkersQueryState = ({
    eventRoles,
    selectedRoleID,
    subcontractorID,
    statusWhere,
} = {}) => {
    const eligibleRoleIDs = normalizeStaffWorkerRoleIDs(eventRoles);
    const where = buildStaffWorkersWhere({
        eligibleRoleIDs,
        selectedRoleID,
        subcontractorID,
        statusWhere,
    });

    return {
        eligibleRoleIDs,
        where,
    };
};

export const normalizeStaffWorkerIDs = (workers = []) => (
    [ ...new Set(
        (Array.isArray(workers) ? workers : [])
            .map((worker) => normalizeID(worker?.id ?? worker))
            .filter(Boolean)
    ) ].sort((left, right) => left.localeCompare(right, undefined, { numeric: true }))
);

export const buildStaffWorkerSessionsWhere = ({ eeID, workerIDs = [] } = {}) => {
    const normalizedEeID = normalizeID(eeID);
    const normalizedWorkerIDs = normalizeStaffWorkerIDs(workerIDs);

    if (!normalizedEeID || !normalizedWorkerIDs.length)
    {
        return undefined;
    }

    return {
        column: "EE_ID",
        operator: "EQ",
        value: normalizedEeID,
        AND: [ {
            column: "USER_ID",
            operator: "IN",
            value: normalizedWorkerIDs,
        } ],
    };
};

const getSessionWorkerID = (session) => normalizeID(
    session?.user_id ?? session?.relUser?.id
);

const getSessionWorkedSeconds = (session) => {
    if (session?.getWorkedHours === null || session?.getWorkedHours === undefined || session?.getWorkedHours === "")
    {
        return undefined;
    }

    const hours = Number(session.getWorkedHours);

    return Number.isFinite(hours) && hours >= 0 ? Math.round(hours * 3600) : undefined;
};

const getSessionWorkerStatus = (session) => {
    // A warning status does not close a session; a checkout timestamp does.
    if (String(session?.check_out_at || "").trim())
    {
        return STAFF_WORKER_STATUS.checkedOut;
    }

    const status = String(session?.getCheckOutStatus || "").trim().toUpperCase();

    if (status === "FAILED_CHECKOUT")
    {
        return STAFF_WORKER_STATUS.failedCheckout;
    }

    if (status === "MISSED_CHECKOUT")
    {
        return STAFF_WORKER_STATUS.missedCheckout;
    }

    return STAFF_WORKER_STATUS.checkedIn;
};

export const getStaffWorkerSessionSummaries = (workers = [], sessions = []) => {
    const summaries = normalizeStaffWorkerIDs(workers).reduce((result, workerID) => ({
        ...result,
        [ workerID ]: {
            status: STAFF_WORKER_STATUS.notCheckedIn,
            totalWorkedSeconds: null,
        },
    }), {});
    const aggregation = {};

    (Array.isArray(sessions) ? sessions : []).filter(Boolean).forEach((session) => {
        const workerID = getSessionWorkerID(session);

        if (!workerID || !summaries[ workerID ])
        {
            return;
        }

        const sessionStatus = getSessionWorkerStatus(session);
        const workerAggregation = aggregation[ workerID ] || {
            hasClosedSession: false,
            hasIncompleteHours: false,
            status: STAFF_WORKER_STATUS.checkedOut,
            totalWorkedSeconds: 0,
        };

        if (WORKER_STATUS_PRIORITY[ sessionStatus ] > WORKER_STATUS_PRIORITY[ workerAggregation.status ])
        {
            workerAggregation.status = sessionStatus;
            workerAggregation.viewSessionID = normalizeID(session.id);
        }
        else if (sessionStatus === workerAggregation.status && !workerAggregation.viewSessionID)
        {
            workerAggregation.viewSessionID = normalizeID(session.id);
        }

        if (sessionStatus === STAFF_WORKER_STATUS.checkedOut)
        {
            const workedSeconds = getSessionWorkedSeconds(session);
            const sessionID = normalizeID(session.id);
            const checkOutAt = String(session.check_out_at).trim();
            workerAggregation.hasClosedSession = true;

            // Backend checkout timestamps have a sortable fixed format; IDs only break ties.
            if (sessionID && (!workerAggregation.latestClosedSessionID ||
                checkOutAt > workerAggregation.latestClosedCheckOutAt ||
                (checkOutAt === workerAggregation.latestClosedCheckOutAt &&
                    sessionID > workerAggregation.latestClosedSessionID)))
            {
                workerAggregation.latestClosedSessionID = sessionID;
                workerAggregation.latestClosedCheckOutAt = checkOutAt;
            }

            if (workedSeconds === undefined)
            {
                workerAggregation.hasIncompleteHours = true;
            }
            else
            {
                workerAggregation.totalWorkedSeconds += workedSeconds;
            }
        }

        aggregation[ workerID ] = workerAggregation;
    });

    Object.entries(aggregation).forEach(([ workerID, workerAggregation ]) => {
        const hasOpenSession = workerAggregation.status !== STAFF_WORKER_STATUS.checkedOut;
        const hasValidTotal = workerAggregation.hasClosedSession && !workerAggregation.hasIncompleteHours;
        const viewSessionID = hasOpenSession ? workerAggregation.viewSessionID : workerAggregation.latestClosedSessionID;

        summaries[ workerID ] = {
            status: workerAggregation.status,
            ...(viewSessionID ? { viewSessionID } : {}),
            totalWorkedSeconds: hasValidTotal ? workerAggregation.totalWorkedSeconds : null,
            ...(hasValidTotal && hasOpenSession ? { isTotalPartial: true } : {}),
            ...(workerAggregation.hasIncompleteHours ? { hasIncompleteHours: true } : {}),
        };
    });

    return summaries;
};

export const getStaffWorkerStatusFilter = ({ status, sessions = [] } = {}) => {
    if (!Object.values(STAFF_WORKER_STATUS).includes(status))
    {
        return { where: undefined, noMatches: false };
    }

    const workerIDs = normalizeStaffWorkerIDs(sessions.map(getSessionWorkerID));

    if (status === STAFF_WORKER_STATUS.notCheckedIn)
    {
        return {
            where: workerIDs.length ? { column: "ID", operator: "NOT_IN", value: workerIDs } : undefined,
            noMatches: false,
        };
    }

    const summaries = getStaffWorkerSessionSummaries(workerIDs, sessions);
    const matchingIDs = workerIDs.filter(workerID => summaries[ workerID ].status === status);

    return {
        where: matchingIDs.length ? { column: "ID", operator: "IN", value: matchingIDs } : undefined,
        noMatches: !matchingIDs.length,
    };
};

export const formatStaffWorkerDuration = (seconds) => {
    if (seconds === null || seconds === undefined || !Number.isFinite(Number(seconds)) || Number(seconds) < 0)
    {
        return undefined;
    }

    const normalizedSeconds = Number(seconds);
    const hours = Math.floor(normalizedSeconds / 3600);
    const minutes = Math.floor((normalizedSeconds % 3600) / 60);

    return `${ hours } h ${ minutes } min`;
};
